import { useState, useEffect } from "react";
import { Target, CheckCircle2, CircleDashed, Search, X } from "lucide-react";
import QuestionSelector from "../components/QuestionSelector";
import ProblemList from "../components/ProblemList";
import FloatingIcons from "../components/FloatingIcons";
import StatusTabs from "../components/StatusTabs";
import { useAppContext } from "../context/AppContext";
import Pagination from "../components/Pagination";

import { fetchProblemsByQuestion, fetchSolved } from "../services/api";

function ContestDrillPage({ user }) {
  const { selectedQuestion, setSelectedQuestion, hideRating, setHideRating } = useAppContext();

  const [activeTab, setActiveTab] = useState("unsolved");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [problems, setProblems] = useState([]);
  const [solvedProblems, setSolvedProblems] = useState([]);

  const [loadingUnsolved, setLoadingUnsolved] = useState(false);
  const [loadingSolved, setLoadingSolved] = useState(false);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [solvedPage, setSolvedPage] = useState(1);
  const [solvedTotal, setSolvedTotal] = useState(0);

  const limit = 10;

  const fetchUnsolved = async () => {
    try {
      setLoadingUnsolved(true);

      const res = await fetchProblemsByQuestion(
        selectedQuestion,
        user?.uid,
        page,
        limit,
        debouncedSearch
      );

      setProblems(res.data);
      setTotal(res.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingUnsolved(false);
    }
  };

  const fetchSolvedData = async () => {
    if (!user) return;

    try {
      setLoadingSolved(true);

      const res = await fetchSolved(
        user.uid,
        selectedQuestion,
        null,
        null,
        solvedPage,
        limit,
        debouncedSearch
      );

      setSolvedProblems(res.data);
      setSolvedTotal(res.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingSolved(false);
    }
  };

  useEffect(() => {
    fetchUnsolved();
  }, [selectedQuestion, page, user, debouncedSearch]);

  useEffect(() => {
    fetchSolvedData();
  }, [activeTab, selectedQuestion, user, solvedPage, debouncedSearch]);

  // Debounce: update debouncedSearch 400ms after the user stops typing
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
      setSolvedPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
    setSolvedPage(1);
    setSearchQuery("");
    setDebouncedSearch("");
  }, [selectedQuestion]);

  const handleSolved = (slug) => {
    const moved = problems.find((p) => p.title_slug === slug);
    if (!moved) return;

    const updated = problems.filter((p) => p.title_slug !== slug);
    setProblems(updated);
    setTotal((t) => t - 1);

    setSolvedProblems((prev) => {
      if (prev.some((p) => p.title_slug === slug)) return prev;
      return [moved, ...prev];
    });
    setSolvedTotal((t) => t + 1);

    if (updated.length === 0 && page > 1) {
      setPage((p) => p - 1);
    }
  };

  const handleUnsolved = (slug) => {
    const moved = solvedProblems.find((p) => p.title_slug === slug);
    if (!moved) return;

    setSolvedProblems((prev) => prev.filter((p) => p.title_slug !== slug));
    setSolvedTotal((t) => t - 1);

    setProblems((prev) => {
      if (prev.some((p) => p.title_slug === slug)) return prev;
      return [moved, ...prev];
    });
    setTotal((t) => t + 1);
  };

  // Progress snapshot — gives the header a real, at-a-glance signal instead
  // of being purely decorative.
  const combinedTotal = total + solvedTotal;
  const solvedPct = combinedTotal > 0 ? Math.round((solvedTotal / combinedTotal) * 100) : 0;

  return (
    <div className="px-4 space-y-6">
      <FloatingIcons />

      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="flex items-start gap-3 text-center md:text-left">
          <div className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-white/10">
            <Target size={20} className="text-blue-300" />
          </div>
          <div className="space-y-1">
            <h2 className="text-2xl md:text-3xl font-semibold text-white tracking-tight">
              Practice by Question
            </h2>
            <p className="text-sm text-gray-400">
              Train using real contest positions (Q1–Q4)
            </p>
          </div>
        </div>

        {/* solved / unsolved snapshot */}
        {user && (
          <div className="w-full md:w-64 space-y-1.5">
            <div className="flex items-center justify-between text-xs text-gray-400">
              <span className="flex items-center gap-1 text-emerald-400">
                <CheckCircle2 size={12} />
                {solvedTotal} solved
              </span>
              <span className="flex items-center gap-1 text-gray-500">
                <CircleDashed size={12} />
                {total} left
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-blue-400 transition-all duration-500 ease-out"
                style={{ width: `${solvedPct}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* CONTROLS */}
      <div className="sticky top-2 z-20 flex flex-col gap-3">
        {/* SELECTOR */}
        <div className="glass rounded-xl border border-white/5 p-4">
          <QuestionSelector
            value={selectedQuestion}
            onChange={setSelectedQuestion}
          />
        </div>

        {/* SEARCH BAR */}
        <div className="glass rounded-xl border border-white/5 px-4 py-2.5">
          <div className="relative flex items-center">
            <Search size={14} className="absolute left-0 text-gray-500 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or contest…"
              className="w-full bg-transparent pl-5 pr-7 text-sm text-gray-200 placeholder-gray-600 outline-none"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-0 text-gray-500 hover:text-gray-300 transition-colors"
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        {/* TABS + RATING VISIBILITY */}
        <div className="glass flex flex-col-reverse sm:flex-row items-center justify-between gap-3 rounded-xl border border-white/5 px-4 py-3">
          <StatusTabs
            active={activeTab}
            setActive={setActiveTab}
            unsolvedTotal={total}
            solvedTotal={solvedTotal}
          />

          <button
            type="button"
            role="switch"
            aria-checked={!hideRating}
            onClick={() => setHideRating(!hideRating)}
            className="group flex shrink-0 items-center gap-2"
          >
            <span
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ${
                hideRating ? "bg-gray-700" : "bg-blue-500"
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200 ${
                  hideRating ? "translate-x-1" : "translate-x-5"
                }`}
              />
            </span>
            <span className="text-xs text-gray-400 transition-colors group-hover:text-gray-200">
              {hideRating ? "Ratings hidden" : "Ratings visible"}
            </span>
          </button>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="relative space-y-4 min-h-[320px]">

        {/* subtle glow */}
        <div className="absolute inset-0 -z-10 
                        bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 
                        blur-2xl rounded-2xl" />

        {activeTab === "unsolved" ? (
          <>
            <ProblemList
              problems={problems}
              userId={user?.uid}
              onSolved={handleSolved}
              mode="unsolved"
              loading={loadingUnsolved}
              startIndex={(page - 1) * limit}
              hideRating={hideRating}
            />

            <Pagination
              page={page}
              total={total}
              limit={limit}
              onPageChange={setPage}
            />
          </>
        ) : (
          <>
            <ProblemList
              problems={solvedProblems}
              userId={user?.uid}
              onUnsolved={handleUnsolved}
              mode="solved"
              loading={loadingSolved}
              startIndex={(solvedPage - 1) * limit}
              hideRating={hideRating}
            />

            <Pagination
              page={solvedPage}
              total={solvedTotal}
              limit={limit}
              onPageChange={setSolvedPage}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default ContestDrillPage;
